import { useCallback, useSyncExternalStore } from 'react';

export const FAVOURITE_SHOPS_KEY = 'CS_ADMIN_FAVOURITE_SHOPS';
const CHANGE_EVENT = 'cs-admin-favourite-shops';
const EMPTY: string[] = [];

// Cached by the raw stored string so useSyncExternalStore gets a stable snapshot.
let cachedRaw: string | null = null;
let cachedIds: string[] = EMPTY;

const read = (): string[] => {
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(FAVOURITE_SHOPS_KEY);
  } catch {
    // Blocked storage: no favourites rather than a broken page.
  }
  if (raw === cachedRaw) return cachedIds;
  cachedRaw = raw;
  try {
    const parsed = raw ? JSON.parse(raw) : [];
    cachedIds = Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === 'string') : EMPTY;
  } catch {
    cachedIds = EMPTY;
  }
  return cachedIds;
};

const write = (ids: string[]) => {
  try {
    localStorage.setItem(FAVOURITE_SHOPS_KEY, JSON.stringify(ids));
  } catch {
    // Blocked or full storage: the change just doesn't persist.
    return;
  }
  window.dispatchEvent(new Event(CHANGE_EVENT));
};

const subscribe = (onChange: () => void) => {
  const onStorage = (event: StorageEvent) => {
    if (event.key === null || event.key === FAVOURITE_SHOPS_KEY) onChange();
  };
  window.addEventListener(CHANGE_EVENT, onChange);
  window.addEventListener('storage', onStorage);
  return () => {
    window.removeEventListener(CHANGE_EVENT, onChange);
    window.removeEventListener('storage', onStorage);
  };
};

// Favourite shops by shop_id (what settings routes use), in the order they were added.
// Shared through localStorage, so every star and list stays in sync, also across tabs.
export const useFavouriteShops = () => {
  const favourites = useSyncExternalStore(subscribe, read, () => EMPTY);
  const isFavourite = useCallback((shopId: string) => favourites.includes(shopId), [favourites]);
  const toggleFavourite = useCallback((shopId: string) => {
    const current = read();
    write(current.includes(shopId) ? current.filter((id) => id !== shopId) : [...current, shopId]);
  }, []);
  return { favourites, isFavourite, toggleFavourite };
};
